import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { type AssociatedPerson, identityEntityGetAssociatedPersons } from '@column/api';

import { type EntityApiRequestOptions } from './entityApiMode';

interface UseAllAssociatedPersonsOptions {
  enabled?: boolean;
  // Extra request options merged into each page fetch — e.g. the root flow's mode/platform headers.
  requestOptions?: EntityApiRequestOptions;
  // Extra values that scope the cache key (e.g. platform id + mode for the root flow).
  scope?: unknown[];
}

// Loads every page of a business's associated persons. Saving reconciles with a full-replacement
// PUT, so callers need the complete set — a single 100-item page would silently drop links beyond
// the first page. Auto-fetches subsequent pages until `has_more` is false.
export const useAllAssociatedPersons = (entityId: string | undefined, options: UseAllAssociatedPersonsOptions = {}) => {
  const { enabled = true, requestOptions, scope = [] } = options;

  const query = useInfiniteQuery({
    queryKey: ['associatedPersonsAll', entityId, ...scope],
    enabled: enabled && Boolean(entityId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await identityEntityGetAssociatedPersons({
        path: { entity_id: entityId as string },
        query: { limit: 100, starting_after: typeof pageParam === 'string' ? pageParam : undefined },
        signal,
        throwOnError: true,
        ...requestOptions,
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      const rows = lastPage.associated_persons ?? [];
      return rows[rows.length - 1]?.person_entity_id;
    },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  // A fetch can fail on the first page (isError) or on a follow-up page (isFetchNextPageError, which
  // leaves the overall status 'success' with hasNextPage still true). Treat either as a hard stop.
  const isError = query.isError || query.isFetchNextPageError;

  useEffect(() => {
    // Only auto-advance while another page remains and the previous fetch didn't error. Without the
    // error guard the effect would refire the instant isFetchingNextPage flips back to false and
    // hammer the failing page in an endless loop (hasNextPage stays true after a page error).
    if (hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const associatedPersons = useMemo<AssociatedPerson[]>(
    () => (query.data?.pages ?? []).flatMap((page) => page?.associated_persons ?? []),
    [query.data]
  );

  return {
    associatedPersons,
    // Loading while the first page is in flight or another page is (or is about to be) fetched. Once
    // a fetch errors we stop paginating, so this resolves to false instead of hanging on hasNextPage.
    isLoading: !isError && (query.isLoading || isFetchingNextPage || hasNextPage),
    // The full set is trustworthy only once every page loaded successfully with none remaining.
    isComplete: query.isSuccess && !isError && !hasNextPage && !isFetchingNextPage,
    isError,
    refetch: query.refetch,
  };
};
